import React, { memo, useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../assets/contexts/AuthContext';
import { useTheme } from '../../../assets/contexts/ThemeContext';
import { useRightSidebar } from '../../../assets/contexts/RightSidebarContext';
import { usePopup } from '../../../assets/contexts/PopupContext';
// --- 통합검색 비활성화(추후 복구 시 본 줄·아래 주석 블록 해제) ---
// import UnifiedSearch from '../../../components/widgets/UnifiedSearch';
import { getDefaultHeaderBadges } from '../pages/system/HeaderAreaManager';
import AuthorDisplay from '../../../components/widgets/AuthorDisplay';
import NotificationTopRightToast, { TOAST_AUTO_HIDE_ENABLED, TOAST_AUTO_HIDE_SECONDS } from '../../../components/widgets/NotificationTopRightToast';
import { NOTIFICATION_UNREAD_COUNT_REFRESH_EVENT } from '../../../assets/utils/comm/notificationUnreadCountEvents';
import HeaderLanguageSwitcher from './HeaderLanguageSwitcher';
import useHeaderContent from '../../../assets/hooks/useHeaderContent';
import { resolveHeaderBadgeText } from '../../../assets/utils/comm/headerLocalized';

/**
 * 헤더 상단 영역 컴포넌트
 * 역할: 헤더 상단 영역 (환영 메시지, 로그인/회원가입 링크)
 * 성능 최적화: React.memo로 메모이제이션
 */
/** 알림 벨 아이콘 (outline) */
const NotificationBellIcon = ({ className = '', style = {} }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

/** 로그인/로그아웃용 전원 아이콘 */
const PowerIcon = ({ style = {} }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-power" viewBox="0 0 16 16" aria-hidden="true" style={{ marginRight: 6, verticalAlign: 'middle', ...style }}>
    <path d="M7.5 1v7h1V1z"/>
    <path d="M3 8.812a5 5 0 0 1 2.578-4.375l-.485-.874A6 6 0 1 0 11 3.616l-.501.865A5 5 0 1 1 3 8.812"/>
  </svg>
);

const HeaderTop = memo(({ hideAuthLinks = false, showSignupOnly = false } = {}) => {
  useHeaderContent();
  const [headerContentVersion, setHeaderContentVersion] = useState(0);

  React.useEffect(() => {
    const onHeaderContentUpdated = () => setHeaderContentVersion((v) => v + 1);
    window.addEventListener('glow-header-content-updated', onHeaderContentUpdated);
    return () => window.removeEventListener('glow-header-content-updated', onHeaderContentUpdated);
  }, []);
  const UNREAD_COUNT_SNAPSHOT_KEY = 'glow_unread_notification_count';
  const UNREAD_COUNT_SHARED_KEY = 'glow_unread_notification_count_shared';
  const UNREAD_POLL_LEADER_LOCK_KEY = 'glow_unread_poll_leader_lock';
  const UNREAD_LOCK_HEARTBEAT_MS = 20 * 1000;
  /** 서버 application.yml(glow.notification.unread-poll-interval-seconds)와 동기; 로드 전 기본 60초 */
  const [unreadPollIntervalMs, setUnreadPollIntervalMs] = useState(60 * 1000);
  const unreadPollLeaderLockTtlMs = React.useMemo(
    () => Math.max(75 * 1000, unreadPollIntervalMs + 15 * 1000),
    [unreadPollIntervalMs]
  );

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { default: axios } = await import('axios');
        const res = await axios.get('/api/public/glow/notification/unread-poll-interval');
        const sec = Number(res?.data?.unreadPollIntervalSeconds);
        if (cancelled) return;
        if (Number.isFinite(sec) && sec >= 1) {
          setUnreadPollIntervalMs(sec * 1000);
        }
      } catch {
        // 기본값 유지
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { i18n, t } = useTranslation();
  void headerContentVersion;
  const { openConfirm } = usePopup();
  const themeContext = useTheme();
  const themeConfig = themeContext?.themeConfig || {};
  const { open: openRightSidebar } = useRightSidebar();
  const [notificationCount, setNotificationCount] = useState(0);
  const [notificationToastTitle, setNotificationToastTitle] = useState('');
  const [showNotificationToast, setShowNotificationToast] = useState(false);
  const [isNotificationToastClosing, setIsNotificationToastClosing] = useState(false);
  const prevUnreadCountRef = React.useRef(null);
  const toastAutoCloseTimerRef = React.useRef(null);
  const toastCloseAnimTimerRef = React.useRef(null);
  const unreadPollTabIdRef = React.useRef(
    `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  );

  const clearToastTimers = useCallback(() => {
    if (toastAutoCloseTimerRef.current) {
      clearTimeout(toastAutoCloseTimerRef.current);
      toastAutoCloseTimerRef.current = null;
    }
    if (toastCloseAnimTimerRef.current) {
      clearTimeout(toastCloseAnimTimerRef.current);
      toastCloseAnimTimerRef.current = null;
    }
  }, []);

  const closeNotificationToast = useCallback(() => {
    clearToastTimers();
    setIsNotificationToastClosing(true);
    toastCloseAnimTimerRef.current = setTimeout(() => {
      setShowNotificationToast(false);
      setIsNotificationToastClosing(false);
      toastCloseAnimTimerRef.current = null;
    }, 260);
  }, [clearToastTimers]);

  const openNotificationToast = useCallback((delta = 1) => {
    clearToastTimers();
    const nextDelta = Number.isFinite(Number(delta)) && Number(delta) > 0 ? Math.floor(Number(delta)) : 1;
    setNotificationToastTitle(
      nextDelta === 1
        ? t('headerTop.notificationToastDefault')
        : t('headerTop.notificationToastCount', { count: nextDelta })
    );
    setShowNotificationToast(true);
    setIsNotificationToastClosing(false);
    if (TOAST_AUTO_HIDE_ENABLED) {
      toastAutoCloseTimerRef.current = setTimeout(() => {
        closeNotificationToast();
      }, Math.max(1, Number(TOAST_AUTO_HIDE_SECONDS) || 5) * 1000);
    }
  }, [clearToastTimers, closeNotificationToast, t]);

  /** 미읽음 증가 시 토스트 — ref·sessionStorage 스냅샷 대비 */
  const maybeNotifyUnreadIncrease = useCallback(
    (prevCount, nextCount, suppressToast) => {
      if (suppressToast) return;
      const prev = Number(prevCount);
      const next = Number(nextCount);
      if (!Number.isFinite(next) || next < 0) return;
      let compareBase = Number.isFinite(prev) ? prev : null;
      if (compareBase === null && typeof window !== 'undefined') {
        const saved = Number(window.sessionStorage.getItem(UNREAD_COUNT_SNAPSHOT_KEY));
        if (Number.isFinite(saved) && saved >= 0) {
          compareBase = saved;
        }
      }
      if (compareBase !== null && next > compareBase) {
        openNotificationToast(next - compareBase);
      }
    },
    [openNotificationToast, UNREAD_COUNT_SNAPSHOT_KEY]
  );

  const applyUnreadCountValue = useCallback(
    (count, { suppressToast = false, writeShared = true } = {}) => {
      const prev = prevUnreadCountRef.current;
      prevUnreadCountRef.current = count;
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(UNREAD_COUNT_SNAPSHOT_KEY, String(count));
        if (writeShared) {
          window.localStorage.setItem(
            UNREAD_COUNT_SHARED_KEY,
            JSON.stringify({ count, updatedAt: Date.now() })
          );
        }
      }
      setNotificationCount(count);
      window.__unreadNotificationCount = count;
      window.dispatchEvent(new CustomEvent('notificationUnreadCountUpdated', { detail: { count } }));
      maybeNotifyUnreadIncrease(prev, count, suppressToast);
    },
    [UNREAD_COUNT_SNAPSHOT_KEY, UNREAD_COUNT_SHARED_KEY, maybeNotifyUnreadIncrease]
  );

  const fetchUnreadCount = useCallback(
    async (options = {}) => {
      const suppressToast = options.suppressToast === true;
      if (!isAuthenticated) return;
      try {
        const { default: axios } = await import('axios');
        const res = await axios.get('/api/notifications/my/unread-count', { withCredentials: true });
        const raw = res?.data ?? res;
        const count = Number(raw ?? 0);
        if (!Number.isFinite(count) || count < 0) return;
        applyUnreadCountValue(count, { suppressToast, writeShared: true });
      } catch (err) {
        console.error('안 읽은 알림 개수 조회 실패', err);
      }
    },
    [isAuthenticated, applyUnreadCountValue]
  );

  // 안 읽은 알림 개수:
  // 1) 현재 보고 있는 탭(visibility=visible)에서만 동작
  // 2) 여러 탭 중 "리더 탭" 1개만 폴링
  // 3) 리더가 갱신한 unread 값을 localStorage로 공유해 다른 탭도 표시 동기화
  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const parseLock = (raw) => {
      if (!raw) return null;
      try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        const tabId = typeof parsed.tabId === 'string' ? parsed.tabId : null;
        const expiresAt = Number(parsed.expiresAt);
        if (!tabId || !Number.isFinite(expiresAt)) return null;
        return { tabId, expiresAt };
      } catch {
        return null;
      }
    };

    const now = () => Date.now();
    const currentTabId = unreadPollTabIdRef.current;

    const isCurrentTabLeader = () => {
      const lock = parseLock(window.localStorage.getItem(UNREAD_POLL_LEADER_LOCK_KEY));
      return !!lock && lock.tabId === currentTabId && lock.expiresAt > now();
    };

    const writeLeaderLock = () => {
      const lock = {
        tabId: currentTabId,
        expiresAt: now() + unreadPollLeaderLockTtlMs,
      };
      window.localStorage.setItem(UNREAD_POLL_LEADER_LOCK_KEY, JSON.stringify(lock));
    };

    const tryAcquireLeaderLock = () => {
      const lock = parseLock(window.localStorage.getItem(UNREAD_POLL_LEADER_LOCK_KEY));
      const expired = !lock || lock.expiresAt <= now();
      const mine = lock && lock.tabId === currentTabId;
      if (expired || mine) {
        writeLeaderLock();
        return true;
      }
      return false;
    };

    const releaseLeaderLock = () => {
      if (!isCurrentTabLeader()) return;
      window.localStorage.removeItem(UNREAD_POLL_LEADER_LOCK_KEY);
    };

    let pollIntervalId = null;
    let heartbeatIntervalId = null;

    const stopPoll = () => {
      if (pollIntervalId) {
        clearInterval(pollIntervalId);
        pollIntervalId = null;
      }
      if (heartbeatIntervalId) {
        clearInterval(heartbeatIntervalId);
        heartbeatIntervalId = null;
      }
    };

    const startLeaderPolling = () => {
      if (pollIntervalId) return;
      // 리더 탭에서 즉시 1회 동기화 후 주기 폴링
      fetchUnreadCount({ suppressToast: false });
      pollIntervalId = setInterval(
        () => fetchUnreadCount({ suppressToast: false }),
        unreadPollIntervalMs
      );
      heartbeatIntervalId = setInterval(() => {
        // 리더 락 갱신(탭이 살아있음을 표시)
        if (document.visibilityState === 'visible' && isCurrentTabLeader()) {
          writeLeaderLock();
        }
      }, UNREAD_LOCK_HEARTBEAT_MS);
    };

    const applyPollingRole = () => {
      if (!isAuthenticated) {
        stopPoll();
        releaseLeaderLock();
        return;
      }
      if (document.visibilityState !== 'visible') {
        stopPoll();
        // 보이지 않는 탭은 리더 권한을 내려놓아 다른 가시 탭이 폴링하도록 한다.
        releaseLeaderLock();
        return;
      }
      const leader = tryAcquireLeaderLock();
      if (leader) {
        startLeaderPolling();
      } else {
        stopPoll();
      }
    };

    if (!isAuthenticated) {
      stopPoll();
      setNotificationCount(0);
      prevUnreadCountRef.current = null;
      window.sessionStorage.removeItem(UNREAD_COUNT_SNAPSHOT_KEY);
      return undefined;
    }

    // 배지·토스트 기준값 초기화(다른 탭/이전 방문 스냅샷 — 토스트는 증가분만)
    try {
      const sharedRaw = window.localStorage.getItem(UNREAD_COUNT_SHARED_KEY);
      if (sharedRaw) {
        const payload = JSON.parse(sharedRaw);
        const sharedCount = Number(payload?.count);
        if (Number.isFinite(sharedCount) && sharedCount >= 0) {
          setNotificationCount(sharedCount);
          window.__unreadNotificationCount = sharedCount;
          if (prevUnreadCountRef.current === null) {
            prevUnreadCountRef.current = sharedCount;
          }
        }
      } else {
        const saved = Number(window.sessionStorage.getItem(UNREAD_COUNT_SNAPSHOT_KEY));
        if (Number.isFinite(saved) && saved >= 0) {
          setNotificationCount(saved);
          window.__unreadNotificationCount = saved;
        }
      }
    } catch {
      // no-op
    }

    // 리더 탭에서 동기화한 unread 값 구독(비리더 탭 표시·토스트)
    const handleStorage = (event) => {
      if (!event) return;
      if (event.key === UNREAD_COUNT_SHARED_KEY && event.newValue) {
        try {
          const payload = JSON.parse(event.newValue);
          const next = Number(payload?.count);
          if (Number.isFinite(next) && next >= 0) {
            applyUnreadCountValue(next, { suppressToast: false, writeShared: false });
          }
        } catch {
          // no-op
        }
      }
      if (event.key === UNREAD_POLL_LEADER_LOCK_KEY) {
        // 리더 탭 변경 시 역할 재평가
        applyPollingRole();
      }
    };

    const handleVisibilityChange = () => {
      applyPollingRole();
    };

    window.addEventListener('storage', handleStorage);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    applyPollingRole();

    return () => {
      stopPoll();
      releaseLeaderLock();
      window.removeEventListener('storage', handleStorage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    isAuthenticated,
    fetchUnreadCount,
    applyUnreadCountValue,
    UNREAD_COUNT_SNAPSHOT_KEY,
    UNREAD_COUNT_SHARED_KEY,
    UNREAD_POLL_LEADER_LOCK_KEY,
    unreadPollLeaderLockTtlMs,
    unreadPollIntervalMs,
    UNREAD_LOCK_HEARTBEAT_MS
  ]);

  // 종 클릭·알림 읽음 등에서 즉시 재조회 (토스트 없음)
  React.useEffect(() => {
    if (!isAuthenticated) return undefined;
    const handler = () => {
      fetchUnreadCount({ suppressToast: true });
    };
    window.addEventListener(NOTIFICATION_UNREAD_COUNT_REFRESH_EVENT, handler);
    return () => window.removeEventListener(NOTIFICATION_UNREAD_COUNT_REFRESH_EVENT, handler);
  }, [isAuthenticated, fetchUnreadCount]);

  React.useEffect(() => () => clearToastTimers(), [clearToastTimers]);

  // --- 통합검색 핸들러·설정 (비활성화, 추후 복구 시 주석 해제 + UnifiedSearch import + useMemo import) ---
  // const handleSearch = useCallback((keyword) => {
  //   console.log('통합검색:', keyword);
  //   // TODO: 통합검색 로직 구현
  //   // 예: 검색 결과 페이지로 이동하거나 검색 API 호출
  //   if (keyword && keyword.trim()) {
  //     // 검색 로직 구현
  //     // window.location.href = `/search?q=${encodeURIComponent(keyword)}`;
  //   }
  // }, []);
  //
  // const searchComponent = useMemo(() => ({
  //   id: 'header-unified-search',
  //   placeholder: '검색...',
  //   onSearch: handleSearch,
  //   debounceMs: 300,
  //   showButton: false,
  //   size: 'sm'
  // }), [handleSearch]);

  const handleNotificationClick = useCallback(() => {
    closeNotificationToast();
    fetchUnreadCount({ suppressToast: true });
    openRightSidebar('notification');
  }, [closeNotificationToast, fetchUnreadCount, openRightSidebar]);

  const handleLogoutClick = useCallback((e) => {
    e.preventDefault();
    openConfirm({
      title: t('headerTop.logoutConfirmTitle'),
      content: t('headerTop.logoutConfirmMessage'),
    }).then(async (result) => {
      if (result?.submit) {
        await logout();
        navigate('/login', { replace: true });
      }
    });
  }, [openConfirm, logout, navigate, t]);

  if (themeConfig.headerTopVisible === false) {
    return null;
  }

  return (
    <div
      className="header-top d-none d-lg-block bg-gray"
      style={{
        backgroundColor: themeConfig.headerTopBackgroundColor || '#f8f8f8',
        color: themeConfig.headerTopTextColor || '#000000',
        paddingTop: `${themeConfig.headerTopHeight || 6}px`,
        paddingBottom: `${themeConfig.headerTopHeight || 6}px`,
        borderBottom: '1px solid #eff1f3'
      }}
    >
      <div className="container">
        <style>{`
          .header-top-notification-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 4px;
            margin: 0;
            border: none;
            background: transparent;
            cursor: pointer;
            border-radius: 8px;
            transition: background 0.2s, opacity 0.2s;
          }
          .header-top-notification-btn:hover {
            background: rgba(0, 0, 0, 0.06);
            opacity: 0.9;
          }
          .header-top-notification-icon-wrap {
            position: relative;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }
          .header-top-notification-icon {
            display: block;
          }
          .header-top-notification-badge {
            position: absolute;
            top: -6px;
            right: -6px;
            min-width: 16px;
            height: 16px;
            padding: 0 4px;
            font-size: 10px;
            font-weight: 700;
            line-height: 16px;
            color: #fff;
            background: var(--theme-color-primary, #e97730);
            border: 1px solid #fff;
            border-radius: 8px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            box-sizing: border-box;
          }
          .header-top-user-wrap {
            display: inline-flex;
            align-items: center;
            gap: 6px;
          }
          .header-top-user-initial {
            width: 22px;
            height: 22px;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: 600;
            color: #fff;
            flex-shrink: 0;
          }
          .header-top-user-link .header-top-user-alliance {
            display: inline-flex;
            align-items: center;
            gap: 6px;
          }
          .header-top-user-link .header-top-user-alliance .alli.alli-author {
            flex-wrap: nowrap;
          }
          .header-top-user-link .header-top-user-alliance .alli-author-initial {
            width: 22px;
            height: 22px;
            min-width: 22px;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: 600;
            color: #fff;
            background-color: var(--header-user-bg, #000);
            flex-shrink: 0;
          }
          .header-top-user-link .header-top-user-alliance .alli-author-name,
          .header-top-user-link .header-top-user-alliance .alli-author-org,
          .header-top-user-link .header-top-user-greeting {
            color: var(--header-user-color, #000);
            font-weight: 700;
            border-color: var(--header-user-color, #000);
          }
          .header-top-user-link .header-top-user-alliance .alli-author-org{
            padding: 1px 10px !important;
          }
          .header-top .user-login-links{
            gap: 2px;
          }
          .header-top .user-login-links .header-top-auth-link {
            display: inline-flex;
            align-items: center;
            padding: 4px 8px;
            margin: -4px -2px;
            border-radius: 8px;
            text-decoration: none;
            background: transparent;
            border: none;
            transition: background 0.2s, opacity 0.2s;
          }
          .header-top .user-login-links .header-top-auth-link:hover {
            background: rgba(0, 0, 0, 0.06);
            opacity: 0.9;
            text-decoration: none;
          }
          .header-top .user-login-links button.header-top-auth-link:hover {
            text-decoration: none;
          }
          .header-top-user-link {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 4px;
            margin: 0 -4px 0 0;
            border: none;
            border-radius: 8px;
            background: transparent;
            cursor: pointer;
            text-decoration: none;
            transition: background 0.2s, opacity 0.2s;
          }
          .header-top-user-link:hover {
            background: rgba(0, 0, 0, 0.06);
            opacity: 0.9;
            text-decoration: none;
          }
          .header-top-badge {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            padding: 5px 10px;
            border-radius: 16px;
            font-size: 11px;
            font-weight: 600;
            white-space: nowrap;
            line-height: 1;
            box-sizing: border-box;
          }
          .header-top-badge-icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
          /* Themify 글리프는 글자 상자 안에서 위쪽에 몰려 보이는 경우가 많아 살짝 내린다 */
          .header-top-badge-icon i[class^="ti-"] {
            font-size: 11px;
            line-height: 1;
            font-style: normal;
            display: block;
            transform: translateY(1px);
          }
          .header-top-badge-emoji {
            font-size: 11px;
            line-height: 1;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            transform: translateY(0.5px);
          }
          .header-top-badge-text {
            display: inline-block;
            line-height: 1;
            font-size: 11px;
          }
        `}</style>
        <div className="row align-items-center">
          <div className="col-lg-6 col-6 d-flex align-items-center gap-1">
            {/* 배지: Header 영역 관리에서 설정한 데이터 우선, 없으면 기본 2개 */}
            {(() => {
              const headerData = typeof window !== 'undefined' ? window.headerContent?.headerData : null;
              const badgeList = Array.isArray(headerData?.badges)
                ? headerData.badges
                : getDefaultHeaderBadges();
              const renderHeaderBadgeIcon = (icon) => {
                const wrap = (node) => <span className="header-top-badge-icon">{node}</span>;
                if (!icon) return wrap(<span className="header-top-badge-emoji" aria-hidden="true">📌</span>);
                if (icon === 'check') return wrap(<i className="ti-check" aria-hidden="true" />);
                if (typeof icon === 'string' && icon.startsWith('ti-')) {
                  return wrap(<i className={icon} aria-hidden="true" />);
                }
                return wrap(<span className="header-top-badge-emoji">{icon}</span>);
              };
              return badgeList.map((b, i) => (
                <div
                  key={i}
                  className="header-top-badge"
                  style={{
                    backgroundColor: b.backgroundColor || '#e3f2fd',
                    border: `1px solid ${b.borderColor || '#90caf9'}`,
                    color: b.color || '#1565c0'
                  }}
                >
                  {renderHeaderBadgeIcon(b.icon)}
                  <span className="header-top-badge-text">
                    {resolveHeaderBadgeText(b, i18n.language)}
                  </span>
                </div>
              ));
            })()}
            <HeaderLanguageSwitcher textColor={themeConfig.headerTopTextColor || '#374151'} />
          </div>
          <div className="col-lg-6 col-6 text-end d-flex justify-content-end align-items-center gap-3">
            {/* --- 통합검색 UI 비활성화(추후 복구: 아래 블록 주석 해제 + 상단 import·handleSearch·searchComponent 주석 해제, props에 hideSearch 복구) ---
            {!hideSearch && (
              <div style={{ position: 'relative' }}>
                <style>
                  {`
                    #header-unified-search .unified-search-input {
                      height: 24px !important;
                      font-size: 11px !important;
                      padding: 0 10px 0 32px !important;
                      line-height: 1.2 !important;
                    }
                    #header-unified-search .unified-search-icon {
                      font-size: 12px !important;
                      left: 10px !important;
                    }
                  `}
                </style>
                <UnifiedSearch component={searchComponent} />
              </div>
            )}
            */}
            {!hideAuthLinks && (
              <div className="user-login-links">
                {showSignupOnly ? (
                  <Link to="/register" className="header-top-auth-link" style={{ color: themeConfig.headerTopTextColor || '#000000' }}>{t('login.signup')}</Link>
                ) : !isAuthenticated ? (
                  <>
                    <Link to="/login" className="header-top-auth-link" style={{ color: themeConfig.headerTopTextColor || '#000000' }}>
                      <PowerIcon />
                      {t('login.submit')}
                    </Link>
                    <span className="divider" style={{ color: themeConfig.headerTopTextColor || '#000000' }}>ㆍ</span>
                    <Link to="/register" className="header-top-auth-link" style={{ color: themeConfig.headerTopTextColor || '#000000' }}>{t('login.signup')}</Link>
                  </>
                ) : (
                  <>
                    <Link
                      to="/mypage/my-info"
                      className="header-top-user-link header-top-user-wrap"
                      style={{
                        '--header-user-bg': themeConfig.primaryColor || themeConfig.themeColor || '#000000',
                        '--header-user-color': themeConfig.primaryColor || themeConfig.themeColor || '#000000'
                      }}
                    >
                      <span className="header-top-user-alliance">
                        <AuthorDisplay
                          regUid={user?.uid ?? user?.id}
                          name={user?.userNm ?? user?.userName ?? ''}
                          userId={user?.userId ?? ''}
                          org={user?.companyName ?? user?.orgName ?? user?.orgNm ?? ''}
                          className="header-top-user-alliance-author"
                        />
                      </span>
                      <span className="header-top-user-greeting">{t('headerTop.userHonorific')}</span>
                    </Link>
                    <span className="divider" style={{ color: themeConfig.headerTopTextColor || '#000000' }}>ㆍ</span>
                    <Link
                      to="#"
                      className="header-top-auth-link"
                      style={{ color: themeConfig.headerTopTextColor || '#000000' }}
                      onClick={handleLogoutClick}
                    >
                      <PowerIcon />
                      {t('headerTop.logout')}
                    </Link>
                  </>
                )}
              </div>
            )}
            {isAuthenticated && (
              <button
                type="button"
                className="header-top-notification-btn"
                onClick={handleNotificationClick}
                aria-label={t('headerTop.notificationViewAria')}
                style={{ color: themeConfig.headerTopTextColor || '#000000' }}
              >
                <span className="header-top-notification-icon-wrap">
                  <NotificationBellIcon className="header-top-notification-icon" style={{ width: 20, height: 20 }} />
                  {notificationCount > 0 && (
                    <span className="header-top-notification-badge" aria-hidden="true">
                      {notificationCount > 99 ? '99+' : notificationCount}
                    </span>
                  )}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
      <NotificationTopRightToast
        visible={showNotificationToast}
        closing={isNotificationToastClosing}
        title={notificationToastTitle}
        description={t('headerTop.notificationToastDescription')}
        onClick={handleNotificationClick}
        onClose={closeNotificationToast}
      />
    </div>
  );
});

HeaderTop.displayName = 'HeaderTop';

export default HeaderTop;

